<template>
    <div ref="smart_render_element" class="smart-render" :style="'height: ' + viewport_element_height + 'px'">
        <div ref="viewport_element" class="smart-render-viewport" :style='"transform: translateY(" + viewport_element_position + "px)"'>
            <slot name="elements"> </slot>
        </div>
    </div>
</template>

<script setup>
  import { ref, defineProps, defineEmits, defineExpose, onMounted, nextTick, watch, onBeforeUnmount } from "vue";

  const emit = defineEmits([
    'smartListItemsSet',
    'smartListScrollIndexSet',
    'smartListItemsLoaded'
  ])

  const props = defineProps({
    smart_list_items : {
      type     : Array,
      required : true,
    },

    viewport_limit : {
      type    : Number,
      default : 50,
    },

    viewport_threshold : {
      type    : Number,
      default : 5,
    },
  });


  const smart_render_element         = ref();
  const viewport_element             = ref();
  const viewport_element_height      = ref(0);
  const viewport_element_position    = ref(0);
  const viewport_element_item_height = ref(0);
  const viewport = {
    items         : [],
    limit         : props.viewport_limit,
    threshold     : props.viewport_threshold,
    first         : 0,
    last          : 0,
    visible_items : 0,
    item_height   : 0,
  }

  const scroll = {
    element       : null,
    index         : 0,
    direction     : null,
  }

  watch(() => props.smart_list_items, () => {
    moveViewportByIndex(viewport.first, true);
    setViewportHeight();
  });

  watch(props.smart_list_items, () => {
    moveViewportByIndex(viewport.first, true);
    setViewportHeight();
  });

  onMounted(async () => {
    moveViewportByIndex(0);
    await nextTick();
    setScrollIndex(0);
    setViewportHeight();
    setScrollElement();
    setEventListeners();
    emit('smartListItemsLoaded')
  })

  onBeforeUnmount(() => {
    unsetEventListeners();
  })

  const onViewportScroll = (event) => {
    let index = calculateScrollIndex(event.target.scrollTop);

    if (index === scroll.index) {
      return;
    }

    setViewportVisibleItems(event.target.offsetHeight);
    setScrollDirection(index);
    setScrollIndex(index);
    moveViewportByDirection(scroll.direction);
  }

  const scrollToItem = (item) => {
    let index = props.smart_list_items.indexOf(item);

    if (index === -1) {
      return;
    }

    scrollToIndex(index);
  }

  const scrollToIndex = (index) => {
    if (!scroll.element) {
      return;
    }

    setViewportVisibleItems(scroll.element.offsetHeight);
    index = index - (viewport.visible_items / 2);
    moveViewportByIndex(index);
    moveScroll(index)
  }

  const moveScroll = (index) => {
    scroll.element.scrollTop = (index * viewport_element_item_height.value);
  }

  const moveViewportByDirection = (direction) => {
    if (direction === 'down') {
      moveViewportDown();
      return;
    }

    if (direction === 'up') {
      moveViewportUp();
      return;
    }
  }

  const moveViewportDown = () => {
    if (viewport.last >= props.smart_list_items.length) {
      return;
    }

    if (scroll.index >= viewport.last - viewport.visible_items - viewport.threshold) {
      moveViewportByIndex(scroll.index - viewport.threshold);
    }
  }

  const moveViewportUp = () => {
    if (viewport.first <= 0) {
      return;
    }

    if (scroll.index <= viewport.first + viewport.threshold) {
      moveViewportByIndex(scroll.index - viewport.limit + viewport.visible_items);
    }
  }

  const moveViewportByIndex = (index, force = false) => {
    let first = index;
    let last  = 0;

    if (first + viewport.limit >= props.smart_list_items.length) {
      first = props.smart_list_items.length - viewport.limit;
    }

    if (first <= 0) {
      first = 0;
    }

    last = first + viewport.limit;

    if (last >= props.smart_list_items.length) {
      last = props.smart_list_items.length;
    }

    if (first === viewport.first && last === viewport.last && force === false) {
      return;
    }

    setViewport(first, last);
    setViewportElementPosition(first);
  }

  const setScrollElement = () => {
    scroll.element = smart_render_element.value.closest('.smart-render-scroll');
  }

  const setScrollIndex = (index) => {
    emit('smartListScrollIndexSet', props.smart_list_items[index]);
    scroll.index = index;
  }

  const setScrollDirection = (index) => {
    scroll.direction = index > scroll.index ? 'down' : 'up';
  }

  const calculateScrollIndex = (top) => {
    return Math.floor(top / viewport_element_item_height.value);
  }

  const setEventListeners = () => {
    scroll.element.addEventListener('scroll', onViewportScroll);
  }

  const unsetEventListeners = () => {
    scroll.element.removeEventListener('scroll', onViewportScroll);
  }

  const setViewport = (first_item, last_item) => {
    viewport.first    = first_item;
    viewport.last     = last_item;
    viewport.items    = props.smart_list_items.slice(viewport.first, viewport.last);
    emit('smartListItemsSet', viewport.items);
  }

  const setViewportVisibleItems = (offset) => {
    viewport.visible_items = Math.floor(offset / viewport_element_item_height.value);
  }

  const setViewportElementPosition = (index) => {
    viewport_element_position.value = index * viewport_element_item_height.value;
  }

  const setViewportHeight = () => {
    if (!viewport_element.value.children[0]) {
      return;
    }

    viewport_element_item_height.value = viewport_element.value.children[0].offsetHeight;
    viewport_element_height.value      = viewport_element_item_height.value * props.smart_list_items.length;
  }

  defineExpose({
    scrollToIndex: scrollToIndex,
    scrollToItem: scrollToItem,
  })
</script>